// role-based-dashboards — Phase 6.2: ungated subscription/usage card widget.
//
// Compact read-only summary of the org's own SaaS subscription (plan,
// status, vehicle usage vs quota) — deep-links to the full SubscriptionPage
// rather than duplicating its invoices/overage-charges/cancel-flow detail
// (spec "deep-link to existing detail pages rather than duplicating them").

import { useTranslation } from '@jq4race/i18n';
import type { SubscriptionResponse } from '@jq4race/shared';
import { Badge, type BadgeProps, Button, Card, CardContent, Skeleton } from '@jq4race/ui';
import { Link } from '@tanstack/react-router';
import { TriangleAlert } from 'lucide-react';
import { translateApiError } from '../../../../shared/errors/translateApiError';
import { useOwnSubscription } from '../../../subscription/ui/hooks/useOwnSubscription';

const STATUS_TONE: Record<SubscriptionResponse['status'], BadgeProps['variant']> = {
  incomplete: 'secondary',
  active: 'success',
  past_due: 'warning',
  suspended: 'danger',
  canceled: 'secondary',
};

export function SubscriptionUsageCard() {
  const { t } = useTranslation('dashboard');
  // Shared with the app-shell banner and the Subscription page (one key).
  const { data, isLoading, isError, error } = useOwnSubscription();

  const STATUS_LABEL: Record<SubscriptionResponse['status'], string> = {
    incomplete: t('subscriptionStatus.incomplete'),
    active: t('subscriptionStatus.active'),
    past_due: t('subscriptionStatus.pastDue'),
    suspended: t('subscriptionStatus.suspended'),
    canceled: t('subscriptionStatus.canceled'),
  };

  return (
    <Card className="border-jq-sand/70 bg-jq-surface-0 shadow-sm">
      <CardContent className="space-y-4 p-5">
        <div className="flex items-center justify-between">
          <p className="jq-eyebrow">{t('subscriptionCard.eyebrow')}</p>
          <Button asChild variant="ghost" size="sm" className="text-[13px]">
            <Link to="/dashboard/settings/subscription">{t('subscriptionCard.manage')}</Link>
          </Button>
        </div>

        {isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-6 w-32" />
            <Skeleton className="h-2 w-full" />
          </div>
        ) : isError ? (
          <p className="flex items-center gap-2 text-[13px] text-jq-danger">
            <TriangleAlert className="h-4 w-4 shrink-0" strokeWidth={1.75} />
            {translateApiError(error, t, { fallback: t('subscriptionCard.error') })}
          </p>
        ) : data ? (
          <div className="space-y-3">
            <div className="flex items-center justify-between gap-3">
              <p className="text-[16px] font-medium text-jq-charcoal">{data.planName}</p>
              <Badge variant={STATUS_TONE[data.status]}>{STATUS_LABEL[data.status]}</Badge>
            </div>
            {data.usage ? (
              <div className="space-y-1.5">
                <div className="flex items-center justify-between text-[12px] text-jq-graphite">
                  <span>
                    {t('subscriptionCard.vehicleUsage', {
                      count: data.usage.vehicleCount,
                      total: data.usage.includedVehicles,
                    })}
                  </span>
                  {data.usage.overageUnits > 0 && (
                    <span className="text-jq-danger">
                      {t('subscriptionCard.overQuota', { count: data.usage.overageUnits })}
                    </span>
                  )}
                </div>
                <div className="h-2 w-full overflow-hidden rounded-full bg-jq-surface-2">
                  <div
                    className={`h-full rounded-full ${
                      data.usage.overageUnits > 0 ? 'bg-jq-danger' : 'bg-jq-success'
                    }`}
                    style={{
                      width: `${
                        data.usage.includedVehicles > 0
                          ? Math.min(
                              100,
                              Math.round(
                                (data.usage.vehicleCount / data.usage.includedVehicles) * 100,
                              ),
                            )
                          : data.usage.vehicleCount > 0
                            ? 100
                            : 0
                      }%`,
                    }}
                  />
                </div>
              </div>
            ) : (
              <p className="text-[13px] text-jq-graphite">{t('subscriptionCard.usagePending')}</p>
            )}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
